import Phaser from 'phaser';
import {
  GAME_FONT_FAMILY,
  GOLD_TEXT_COLOR,
  GOLD_TEXT_COLOR_HEX,
  ITEM_RARITY_COLORS,
  ItemRarity,
} from '@shared/constants';
import type { MerchantItemEntry, MerchantOpenedMessage } from '@shared/types/messages';
import { getItemTemplate, getEffectiveSellPrice } from '@shared/data/items';
import type { ItemDefinition } from '@shared/data/items';
import { Direction } from '@shared/types/entity';
import { getHeadSpriteKey, getHeadFrameForDirection } from '../../sprites/HeadSpriteManager';
import { getBodySpriteKey, getBodyIdleFrame } from '../../sprites/BodySpriteManager';
import { getIconTexture, createCloseButton, formatThousands } from '../../sprites/IconAtlases';
import { ItemTooltip } from './ItemTooltip';

/** Visual de una entity (NPC o player) para el portrait del header. */
export interface MerchantPortraitVisual {
  name: string;
  headId: number;
  bodyId: number;
}

/**
 * Rareza nominal del template para colorear el nombre del item del vendor.
 * - unique → LEGENDARY (modifiers fijos).
 * - setId → SET.
 * - affixable o default → COMMON (al rolear puede ser MAGIC/RARE/EPIC, pero
 *   en el vendor todavía no tiene afijos asignados).
 */
function nominalRarity(tmpl: ItemDefinition): number {
  if (tmpl.unique) return ItemRarity.LEGENDARY;
  if (tmpl.setId) return ItemRarity.SET;
  return ItemRarity.COMMON;
}

/**
 * MerchantPanel — UI estilo MMORPG con DOS listados verticales.
 *
 * Layout (≈760×470):
 *   Header:  portrait NPC + nombre (izq) · título centrado · portrait player + "Tú" (der) · [✕]
 *   Body:    LISTA NPC (comprar) | LISTA JUGADOR (vender). Cada fila = ícono · nombre ·
 *            descripción · cantidad/precio. Scroll con la ruedita sobre cada columna.
 *   Footer:  oro del jugador · barra de cantidad [1][5][10][50][100][500][1000][Máx] ·
 *            botones [Comprar] [Vender].
 *
 * Interacción:
 *   - Click en una fila → selecciona (resalta) y activa el botón que corresponda.
 *   - Hover sobre una fila → resalta + muestra el tooltip del item.
 *   - Rueda del mouse sobre una columna → scrollea esa lista.
 *   - Right-click = comprar/vender 1 · Shift+click = 5 · Shift+right-click = Máx (quick actions).
 *   - Keyboard: Esc cierra · Enter confirma · 1/5/0/M setean cantidad (M = máx).
 *
 * El icon loading sigue el mismo patrón que InventoryPanel (key = `item_icon_<icon>`).
 */
function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

const PLACEHOLDER_ICON = 'item_icon_noicon';

export interface MerchantPanelCallbacks {
  onBuy: (itemId: string, quantity: number) => void;
  onSell: (slotIndex: number, quantity: number) => void;
  onClose?: () => void;
  onMove?: (x: number, y: number) => void;
  /** Resuelve el visual del NPC vendor para el header izquierdo. */
  getNpcVisual?: (npcId: string) => MerchantPortraitVisual | null;
  /** Resuelve el visual del player local para el header derecho. */
  getLocalPlayerVisual?: () => MerchantPortraitVisual | null;
}

export interface MerchantPanelOptions {
  initialX: number;
  initialY: number;
  width: number;
  height: number;
}

interface InventoryRow {
  slotIndex: number;
  itemId: string;
  quantity: number;
  rolledRarity?: number;
  rolledAffixes?: Record<string, Array<{ v: number; t: number }>>;
}

type SelectionSource = 'vendor' | 'inventory';
interface Selection {
  source: SelectionSource;
  /** Para vendor: itemId. Para inventory: slotIndex. */
  key: string | number;
}

const DEPTH_BG = 1100;
const DEPTH_FG = 1101;
const DEPTH_BTN = 1102;

const HEADER_HEIGHT = 36;
/** Alto de cada fila de la lista (ícono 32 + margen para nombre+descripción). */
const ROW_H = 40;
/** Tamaño del ícono del item por fila. */
const ROW_ICON = 32;
/** Padding interno de la columna. */
const COL_PAD = 8;
/** Separación entre las dos columnas. */
const COL_GAP = 14;
/** Padding externo del panel. */
const PANEL_PAD = 12;
/** Alto reservado para el footer (oro + cantidad + botones). */
const FOOTER_H = 78;
/** Tamaño del portrait (head/body) del header — mismo formato que TargetPanel. */
const PORTRAIT_W = 34;
const PORTRAIT_H = 44;
const PORTRAIT_NAME_GAP = 6;
const PORTRAIT_TOP_OFFSET = HEADER_HEIGHT;
const PORTRAIT_BOTTOM_GAP = 8;

/** Fills de la fila según estado (transparente / hover / seleccionada). */
const ROW_FILL_HOVER = 0xffffff;
const ROW_FILL_HOVER_ALPHA = 0.08;
const ROW_FILL_SEL = 0xffd166;
const ROW_FILL_SEL_ALPHA = 0.22;

/** Presets de cantidad de la barra inferior (el último chip es "Máx"). */
const QTY_PRESETS = [1, 5, 10, 50, 100, 500, 1000];

/** Una fila visible de una lista (contenido se rellena según scrollOffset). */
interface ListRow {
  bg: Phaser.GameObjects.Rectangle;
  icon: Phaser.GameObjects.Image;
  name: Phaser.GameObjects.Text;
  desc: Phaser.GameObjects.Text;
  qty: Phaser.GameObjects.Text;
  /** Índice de la fila dentro de su columna (0..visibleRows-1). */
  rowIndex: number;
  source: SelectionSource;
}

/** Un chip de cantidad de la barra inferior. */
interface QtyChip {
  text: Phaser.GameObjects.Text;
  /** Valor del preset, o null para el chip "Máx". */
  value: number | null;
}

export class MerchantPanel {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: MerchantPanelCallbacks;
  private readonly options: MerchantPanelOptions;

  private isOpen = false;
  private isDragging = false;
  private dragOffsetX = 0;
  private dragOffsetY = 0;

  private panelX: number;
  private panelY: number;

  // Estado actual
  private currentNpcId: string | null = null;
  private currentMerchantId: string | null = null;
  private currentItems: MerchantItemEntry[] = [];
  private currentInventory: InventoryRow[] = [];
  private currentGold = 0;
  private selection: Selection | null = null;
  private quantity = 1;
  private scrollVendor = 0;
  private scrollInv = 0;

  // Header / chrome
  private bg!: Phaser.GameObjects.Rectangle;
  private title!: Phaser.GameObjects.Text;
  private closeBtn!: Phaser.GameObjects.Sprite;
  private divider!: Phaser.GameObjects.Rectangle;

  // Portraits + nombre (izq = NPC, der = player).
  private vendorPortraitBg!: Phaser.GameObjects.Rectangle;
  private vendorPortraitSprite: Phaser.GameObjects.Sprite | null = null;
  private vendorNameText!: Phaser.GameObjects.Text;
  private playerPortraitBg!: Phaser.GameObjects.Rectangle;
  private playerPortraitSprite: Phaser.GameObjects.Sprite | null = null;
  private playerNameText!: Phaser.GameObjects.Text;
  private vendorPortraitCX = 0;
  private vendorPortraitCY = 0;
  private playerPortraitCX = 0;
  private playerPortraitCY = 0;

  // Listas
  private vendorListBg!: Phaser.GameObjects.Rectangle;
  private invListBg!: Phaser.GameObjects.Rectangle;
  private vendorRows: ListRow[] = [];
  private invRows: ListRow[] = [];
  private visibleRows = 1;

  // Footer: oro + barra de cantidad + botones.
  private goldLabel!: Phaser.GameObjects.Container;
  private goldLabelIcon!: Phaser.GameObjects.Sprite;
  private goldLabelText!: Phaser.GameObjects.Text;
  private qtyLabel!: Phaser.GameObjects.Text;
  private qtyChips: QtyChip[] = [];

  private buyBtn!: Phaser.GameObjects.Container;
  private buyBtnBg!: Phaser.GameObjects.Rectangle;
  private buyBtnLabel!: Phaser.GameObjects.Text;
  private buyBtnIcon!: Phaser.GameObjects.Sprite;
  private buyBtnAmount!: Phaser.GameObjects.Text;
  private sellBtn!: Phaser.GameObjects.Container;
  private sellBtnBg!: Phaser.GameObjects.Rectangle;
  private sellBtnLabel!: Phaser.GameObjects.Text;
  private sellBtnIcon!: Phaser.GameObjects.Sprite;
  private sellBtnAmount!: Phaser.GameObjects.Text;

  private hintText!: Phaser.GameObjects.Text;

  // Keyboard + wheel
  private keyHandler: ((event: KeyboardEvent) => void) | null = null;
  private wheelHandler:
    | ((p: Phaser.Input.Pointer, o: unknown, dx: number, dy: number) => void)
    | null = null;

  // Tooltip on hover.
  private tooltip!: ItemTooltip;

  constructor(scene: Phaser.Scene, callbacks: MerchantPanelCallbacks, options: MerchantPanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.options = options;
    this.panelX = options.initialX;
    this.panelY = options.initialY;
    this.tooltip = new ItemTooltip(scene);
    this.buildPanel();
    this.attachWheel();
    this.setVisible(false);
    this.applyCameraFilter();
  }

  // === CAMERA FILTER =========================================================

  /**
   * Marca todos los GameObjects del panel como ignorados por `cameras.main`
   * para que rendericen sólo en la `uiCamera`. Los containers NO propagan el
   * ignore a sus children, por eso se listan aparte.
   */
  private applyCameraFilter(): void {
    const main = this.scene.cameras.main;
    if (!main) return;
    const all: Phaser.GameObjects.GameObject[] = [
      this.bg, this.title, this.closeBtn, this.divider,
      this.vendorPortraitBg, this.vendorNameText,
      this.playerPortraitBg, this.playerNameText,
      this.vendorListBg, this.invListBg,
      this.goldLabel, this.goldLabelIcon, this.goldLabelText,
      this.qtyLabel,
      this.buyBtn, this.buyBtnBg, this.buyBtnLabel, this.buyBtnIcon, this.buyBtnAmount,
      this.sellBtn, this.sellBtnBg, this.sellBtnLabel, this.sellBtnIcon, this.sellBtnAmount,
      this.hintText,
    ];
    for (const chip of this.qtyChips) all.push(chip.text);
    for (const r of [...this.vendorRows, ...this.invRows]) {
      all.push(r.bg, r.icon, r.name, r.desc, r.qty);
    }
    if (this.vendorPortraitSprite) all.push(this.vendorPortraitSprite);
    if (this.playerPortraitSprite) all.push(this.playerPortraitSprite);
    main.ignore(all);
  }

  // === PUBLIC API ============================================================

  open(opened: MerchantOpenedMessage, gold: number, inventory: InventoryRow[]): void {
    this.currentNpcId = opened.npcId;
    this.currentMerchantId = opened.merchantId;
    this.currentItems = opened.items;
    this.currentInventory = inventory;
    this.currentGold = gold;
    this.title.setText(opened.merchantName);
    this.quantity = 1;
    this.scrollVendor = 0;
    this.scrollInv = 0;
    // Seleccionar el primer item del vendor por default si hay alguno.
    this.selection = opened.items[0]
      ? { source: 'vendor', key: opened.items[0].itemId }
      : null;
    this.setVisible(true);
    this.isOpen = true;
    this.attachKeyboard();
    this.refreshPortraits(opened.merchantName);
    this.refresh();
  }

  /**
   * Cierra el panel. Si silent=true, no dispara onClose (útil cuando el server
   * ya cerró el vendor).
   */
  close(silent: boolean = false): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.tooltip.hide();
    this.setVisible(false);
    this.detachKeyboard();
    // Disparar onClose ANTES de limpiar currentNpcId — el callback necesita
    // el id para enviar CLOSE_VENDOR al server (si no, el NPC queda en
    // INTERACTING_NPC bloqueando el movimiento del player).
    if (!silent) this.callbacks.onClose?.();
    this.currentNpcId = null;
    this.currentMerchantId = null;
  }

  isOpenPanel(): boolean { return this.isOpen; }
  isDraggingPanel(): boolean { return this.isDragging; }
  getCurrentNpcId(): string | null { return this.currentNpcId; }

  setGold(gold: number): void {
    this.currentGold = gold;
    if (this.isOpen) this.refresh();
  }

  setInventory(inventory: InventoryRow[]): void {
    this.currentInventory = inventory;
    if (this.isOpen) {
      // Si la selección apuntaba a un slot que ya no existe, limpiarla.
      if (this.selection?.source === 'inventory') {
        const slotIdx = this.selection.key as number;
        const stillExists = inventory.some((inv) => inv.slotIndex === slotIdx);
        if (!stillExists) this.selection = null;
      }
      // Clampear el scroll del inventario si se achicó.
      this.scrollInv = Math.min(this.scrollInv, this.maxScroll(inventory.length));
      this.refresh();
    }
  }

  isPointerOver(p: Phaser.Input.Pointer): boolean {
    if (!this.isOpen) return false;
    const w = this.options.width;
    const h = this.options.height;
    return (
      p.x >= this.panelX - w / 2 &&
      p.x <= this.panelX + w / 2 &&
      p.y >= this.panelY - h / 2 &&
      p.y <= this.panelY + h / 2
    );
  }

  handlePointerMove(p: Phaser.Input.Pointer): void {
    if (!this.isDragging) return;
    const newX = p.x - this.dragOffsetX;
    const newY = p.y - this.dragOffsetY;
    this.movePanel(newX, newY);
    this.callbacks.onMove?.(this.panelX, this.panelY);
  }

  handlePointerUp(_p?: Phaser.Input.Pointer): void {
    this.isDragging = false;
  }

  /**
   * Compat: el layout de listas ya no soporta drag de items (se compra/vende
   * por selección + botón + quick-actions). GameScene consulta esto para rutear
   * pointermove/up; siempre false.
   */
  isDraggingItem(): boolean {
    return false;
  }

  // === BUILD =================================================================

  private buildPanel(): void {
    const { width: w, height: h } = this.options;
    const bx = this.panelX - w / 2;
    const by = this.panelY - h / 2;

    this.bg = this.scene.add
      .rectangle(this.panelX, this.panelY, w, h, 0x111111, 0.94)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x666666)
      .setDepth(DEPTH_BG)
      .setScrollFactor(0)
      // Fondo interactivo: absorbe hover/click de objetos del mundo debajo.
      .setInteractive();

    this.title = this.scene.add
      .text(this.panelX, by + 16, '', withGameFont({
        fontSize: '15px', color: '#ffffff', fontStyle: 'bold',
      }))
      .setOrigin(0.5)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    this.title.on('pointerdown', (p: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragOffsetX = p.x - this.panelX;
      this.dragOffsetY = p.y - this.panelY;
      p.event.stopPropagation();
    });

    this.closeBtn = createCloseButton(this.scene, bx + w - 14, by + 14, 14)!
      .setOrigin(1, 0)
      .setDepth(DEPTH_BTN);
    this.closeBtn.on('pointerup', (p: Phaser.Input.Pointer) => {
      this.close();
      p.event.stopPropagation();
    });

    // Layout de columnas.
    const colW = (w - PANEL_PAD * 2 - COL_GAP) / 2;
    const vendorX = bx + PANEL_PAD;
    const invX = vendorX + colW + COL_GAP;
    const listTopY = by + PORTRAIT_TOP_OFFSET + PORTRAIT_H + PORTRAIT_BOTTOM_GAP;
    const listBottomY = by + h - FOOTER_H;
    const listH = listBottomY - listTopY;
    this.visibleRows = Math.max(1, Math.floor(listH / ROW_H));

    // Portraits/nombres como headers de columna.
    this.vendorPortraitCX = vendorX + PORTRAIT_W / 2;
    this.vendorPortraitCY = by + PORTRAIT_TOP_OFFSET + PORTRAIT_H / 2;
    this.vendorPortraitBg = this.scene.add
      .rectangle(this.vendorPortraitCX, this.vendorPortraitCY, PORTRAIT_W, PORTRAIT_H, 0x000000, 0.35)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x445566)
      .setDepth(DEPTH_BG)
      .setScrollFactor(0);
    this.vendorNameText = this.scene.add
      .text(vendorX + PORTRAIT_W + PORTRAIT_NAME_GAP, this.vendorPortraitCY, '', withGameFont({
        fontSize: '13px', color: '#88ccff', fontStyle: 'bold',
        wordWrap: { width: colW - PORTRAIT_W - PORTRAIT_NAME_GAP },
      }))
      .setOrigin(0, 0.5)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    this.playerPortraitCX = invX + PORTRAIT_W / 2;
    this.playerPortraitCY = by + PORTRAIT_TOP_OFFSET + PORTRAIT_H / 2;
    this.playerPortraitBg = this.scene.add
      .rectangle(this.playerPortraitCX, this.playerPortraitCY, PORTRAIT_W, PORTRAIT_H, 0x000000, 0.35)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x665533)
      .setDepth(DEPTH_BG)
      .setScrollFactor(0);
    this.playerNameText = this.scene.add
      .text(invX + PORTRAIT_W + PORTRAIT_NAME_GAP, this.playerPortraitCY, '', withGameFont({
        fontSize: '13px', color: '#ffcc66', fontStyle: 'bold',
        wordWrap: { width: colW - PORTRAIT_W - PORTRAIT_NAME_GAP },
      }))
      .setOrigin(0, 0.5)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    // Fondos de las listas.
    this.vendorListBg = this.scene.add
      .rectangle(vendorX + colW / 2, listTopY + listH / 2, colW, listH, 0x1a1a1a, 0.85)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x444444)
      .setDepth(DEPTH_BG)
      .setScrollFactor(0);
    this.invListBg = this.scene.add
      .rectangle(invX + colW / 2, listTopY + listH / 2, colW, listH, 0x1a1a1a, 0.85)
      .setOrigin(0.5)
      .setStrokeStyle(1, 0x444444)
      .setDepth(DEPTH_BG)
      .setScrollFactor(0);

    // Divider central sutil.
    this.divider = this.scene.add
      .rectangle((vendorX + colW + invX) / 2, listTopY + listH / 2, 1, listH, 0x333333, 1)
      .setOrigin(0.5)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    // Filas de cada lista.
    this.vendorRows = this.buildRows(vendorX, colW, listTopY, 'vendor');
    this.invRows = this.buildRows(invX, colW, listTopY, 'inventory');

    // === Footer ==============================================================
    this.buildFooter(bx, by, w, h);
  }

  /** Crea `visibleRows` filas fijas para una columna. */
  private buildRows(colX: number, colW: number, listTopY: number, source: SelectionSource): ListRow[] {
    const rows: ListRow[] = [];
    const iconCX = colX + COL_PAD + ROW_ICON / 2;
    const textX = colX + COL_PAD + ROW_ICON + 8;
    const textW = colW - COL_PAD - ROW_ICON - 8 - COL_PAD - 44; // 44 reservado a la cantidad/precio
    const qtyX = colX + colW - COL_PAD;

    for (let i = 0; i < this.visibleRows; i += 1) {
      const rowTop = listTopY + i * ROW_H;
      const rowCY = rowTop + ROW_H / 2;

      const bgRect = this.scene.add
        .rectangle(colX + colW / 2, rowCY, colW - 4, ROW_H - 2, ROW_FILL_HOVER, 0)
        .setOrigin(0.5)
        .setStrokeStyle(1, 0x000000, 0)
        .setDepth(DEPTH_BG)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });

      const icon = this.scene.add
        .image(iconCX, rowCY, PLACEHOLDER_ICON)
        .setDisplaySize(ROW_ICON, ROW_ICON)
        .setOrigin(0.5)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0)
        .setVisible(false);

      const name = this.scene.add
        .text(textX, rowTop + 6, '', withGameFont({
          fontSize: '12px', color: '#ffffff', fontStyle: 'bold',
        }))
        .setOrigin(0, 0)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0);

      const desc = this.scene.add
        .text(textX, rowTop + 21, '', withGameFont({
          fontSize: '10px', color: '#9a9a9a',
        }))
        .setOrigin(0, 0)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0);

      const qty = this.scene.add
        .text(qtyX, rowCY, '', withGameFont({
          fontSize: '11px', color: '#ffffff', align: 'right',
        }))
        .setOrigin(1, 0.5)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0);

      const row: ListRow = { bg: bgRect, icon, name, desc, qty, rowIndex: i, source };
      // Guardamos el ancho de texto disponible en el objeto name (data) para truncado.
      name.setData('maxW', textW);
      desc.setData('maxW', textW);

      bgRect.on('pointerover', (p: Phaser.Input.Pointer) => this.onRowOver(row, p));
      bgRect.on('pointermove', (p: Phaser.Input.Pointer) => {
        if (this.tooltip.isShown()) this.showRowTooltip(row, p);
      });
      bgRect.on('pointerout', () => this.onRowOut(row));
      bgRect.on('pointerdown', (p: Phaser.Input.Pointer) => {
        // Right-click se maneja en pointerup (para leer shiftKey coherente).
        if (p.rightButtonDown()) return;
      });
      bgRect.on('pointerup', (p: Phaser.Input.Pointer) => {
        const ev = p.event as MouseEvent;
        const isRight = p.button === 2;
        const shift = !!ev.shiftKey;
        this.handleRowInteraction(row, { isRight, shift });
        p.event.stopPropagation();
      });

      rows.push(row);
    }
    return rows;
  }

  /** Construye la barra inferior: oro + label "Cantidad:" + chips + botones. */
  private buildFooter(bx: number, by: number, w: number, h: number): void {
    const goldRowY = by + h - FOOTER_H + 14;
    const qtyRowY = by + h - FOOTER_H + 40;
    const btnRowY = by + h - 22;

    // Oro del jugador (abajo-izquierda).
    const goldTex = getIconTexture('gold');
    this.goldLabelIcon = this.scene.add
      .sprite(0, 0, goldTex?.textureKey ?? '', goldTex?.frame ?? 0)
      .setOrigin(0, 0.5)
      .setDisplaySize(15, 15)
      .setScrollFactor(0);
    this.goldLabelText = this.scene.add
      .text(16, 0, '0', withGameFont({
        fontSize: '13px', color: GOLD_TEXT_COLOR, fontStyle: 'bold',
      }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    this.goldLabel = this.scene.add
      .container(bx + PANEL_PAD, goldRowY, [this.goldLabelIcon, this.goldLabelText])
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    // Barra de cantidad: "Cantidad:" + chips [1][5]...[Máx], centrados.
    this.qtyLabel = this.scene.add
      .text(0, qtyRowY, 'Cantidad:', withGameFont({
        fontSize: '11px', color: '#cccccc',
      }))
      .setOrigin(0, 0.5)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);

    const chipDefs: Array<{ label: string; value: number | null }> = [
      ...QTY_PRESETS.map((v) => ({ label: String(v), value: v as number | null })),
      { label: 'Máx', value: null },
    ];
    this.qtyChips = chipDefs.map((def) => {
      const text = this.scene.add
        .text(0, qtyRowY, def.label, withGameFont({
          fontSize: '11px', color: '#cccccc', backgroundColor: '#2a2a3a',
        }))
        .setOrigin(0, 0.5)
        .setPadding(7, 4, 7, 4)
        .setDepth(DEPTH_BTN)
        .setScrollFactor(0)
        .setInteractive({ useHandCursor: true });
      text.on('pointerover', () => {
        if (!this.isChipActive(def.value)) text.setBackgroundColor('#3a3a4a');
      });
      text.on('pointerout', () => this.styleChip({ text, value: def.value }));
      text.on('pointerup', (p: Phaser.Input.Pointer) => {
        this.setQuantity(def.value === null ? this.computeMaxQuantity() : def.value);
        p.event.stopPropagation();
      });
      return { text, value: def.value };
    });
    this.layoutQtyBar(bx, w, qtyRowY);

    // Botones de acción: Comprar (izq) / Vender (der).
    const goldForBtn1 = getIconTexture('gold');
    const goldForBtn2 = getIconTexture('gold');

    this.buyBtnBg = this.scene.add
      .rectangle(0, 0, 160, 30, 0x2a3a1f, 1)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    this.buyBtnLabel = this.scene.add
      .text(0, 0, 'Comprar', withGameFont({ fontSize: '13px', color: '#ffffff', fontStyle: 'bold' }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    this.buyBtnIcon = this.scene.add
      .sprite(0, 0, goldForBtn1?.textureKey ?? '', goldForBtn1?.frame ?? 0)
      .setOrigin(0, 0.5)
      .setDisplaySize(14, 14)
      .setScrollFactor(0)
      .setVisible(false);
    this.buyBtnAmount = this.scene.add
      .text(0, 0, '', withGameFont({ fontSize: '13px', color: GOLD_TEXT_COLOR, fontStyle: 'bold' }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    this.buyBtn = this.scene.add
      .container(bx + w * 0.3, btnRowY, [
        this.buyBtnBg, this.buyBtnLabel, this.buyBtnIcon, this.buyBtnAmount,
      ])
      .setDepth(DEPTH_BTN)
      .setScrollFactor(0);
    this.buyBtnBg.on('pointerup', (p: Phaser.Input.Pointer) => {
      this.executeBuy();
      p.event.stopPropagation();
    });

    this.sellBtnBg = this.scene.add
      .rectangle(0, 0, 160, 30, 0x3a2a14, 1)
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setInteractive({ useHandCursor: true });
    this.sellBtnLabel = this.scene.add
      .text(0, 0, 'Vender', withGameFont({ fontSize: '13px', color: '#ffffff', fontStyle: 'bold' }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    this.sellBtnIcon = this.scene.add
      .sprite(0, 0, goldForBtn2?.textureKey ?? '', goldForBtn2?.frame ?? 0)
      .setOrigin(0, 0.5)
      .setDisplaySize(14, 14)
      .setScrollFactor(0)
      .setVisible(false);
    this.sellBtnAmount = this.scene.add
      .text(0, 0, '', withGameFont({ fontSize: '13px', color: GOLD_TEXT_COLOR, fontStyle: 'bold' }))
      .setOrigin(0, 0.5)
      .setScrollFactor(0);
    this.sellBtn = this.scene.add
      .container(bx + w * 0.7, btnRowY, [
        this.sellBtnBg, this.sellBtnLabel, this.sellBtnIcon, this.sellBtnAmount,
      ])
      .setDepth(DEPTH_BTN)
      .setScrollFactor(0);
    this.sellBtnBg.on('pointerup', (p: Phaser.Input.Pointer) => {
      this.executeSell();
      p.event.stopPropagation();
    });

    this.hintText = this.scene.add
      .text(this.panelX, by + h - 6, 'Rueda: scroll · Click: elegir · Der/Shift: rápido · Esc: cerrar', withGameFont({
        fontSize: '8px', color: '#666666',
      }))
      .setOrigin(0.5, 0.5)
      .setDepth(DEPTH_FG)
      .setScrollFactor(0);
  }

  /** Posiciona "Cantidad:" + chips centrados horizontalmente en el panel. */
  private layoutQtyBar(bx: number, w: number, y: number): void {
    const GAP = 4;
    const LABEL_GAP = 8;
    let total = this.qtyLabel.width + LABEL_GAP;
    for (const chip of this.qtyChips) total += chip.text.width + GAP;
    total -= GAP;
    let x = bx + w / 2 - total / 2;
    this.qtyLabel.setPosition(x, y);
    x += this.qtyLabel.width + LABEL_GAP;
    for (const chip of this.qtyChips) {
      chip.text.setPosition(x, y);
      x += chip.text.width + GAP;
    }
  }

  // === WHEEL =================================================================

  private attachWheel(): void {
    if (this.wheelHandler) return;
    this.wheelHandler = (_p, _o, _dx, dy): void => {
      if (!this.isOpen) return;
      const ptr = this.scene.input.activePointer;
      const dir = Math.sign(dy);
      if (dir === 0) return;
      if (this.pointerInColumn(ptr, 'vendor')) {
        this.scrollVendor = Phaser.Math.Clamp(
          this.scrollVendor + dir, 0, this.maxScroll(this.currentItems.length)
        );
        this.renderVendorList();
      } else if (this.pointerInColumn(ptr, 'inventory')) {
        this.scrollInv = Phaser.Math.Clamp(
          this.scrollInv + dir, 0, this.maxScroll(this.currentInventory.length)
        );
        this.renderInventoryList();
      }
    };
    this.scene.input.on('wheel', this.wheelHandler);
  }

  private maxScroll(len: number): number {
    return Math.max(0, len - this.visibleRows);
  }

  /** True si el puntero está sobre el área de la lista de la columna dada. */
  private pointerInColumn(p: Phaser.Input.Pointer, source: SelectionSource): boolean {
    const bg = source === 'vendor' ? this.vendorListBg : this.invListBg;
    const left = bg.x - bg.width / 2;
    const right = bg.x + bg.width / 2;
    const top = bg.y - bg.height / 2;
    const bottom = bg.y + bg.height / 2;
    return p.x >= left && p.x <= right && p.y >= top && p.y <= bottom;
  }

  // === ROW INTERACTION =======================================================

  /** Índice en el array fuente para una fila visible (según scroll). */
  private sourceIndex(row: ListRow): number {
    const offset = row.source === 'vendor' ? this.scrollVendor : this.scrollInv;
    return offset + row.rowIndex;
  }

  private itemAtRow(row: ListRow): { itemId: string } | null {
    const idx = this.sourceIndex(row);
    if (row.source === 'vendor') {
      const it = this.currentItems[idx];
      return it ? { itemId: it.itemId } : null;
    }
    const inv = this.currentInventory[idx];
    return inv ? { itemId: inv.itemId } : null;
  }

  private onRowOver(row: ListRow, p: Phaser.Input.Pointer): void {
    if (!this.itemAtRow(row)) return;
    if (!this.isRowSelected(row)) {
      row.bg.setFillStyle(ROW_FILL_HOVER, ROW_FILL_HOVER_ALPHA);
    }
    this.showRowTooltip(row, p);
  }

  private onRowOut(row: ListRow): void {
    this.styleRowBg(row);
    this.tooltip.hide();
  }

  private showRowTooltip(row: ListRow, p: Phaser.Input.Pointer): void {
    const idx = this.sourceIndex(row);
    if (row.source === 'inventory') {
      const inv = this.currentInventory[idx];
      if (!inv) return;
      this.tooltip.show(inv.itemId, p.x, p.y, {
        rolledRarity: inv.rolledRarity,
        rolledAffixes: inv.rolledAffixes,
      });
    } else {
      const item = this.currentItems[idx];
      if (!item) return;
      this.tooltip.show(item.itemId, p.x, p.y, { buyPrice: item.buyPrice });
    }
  }

  private isRowSelected(row: ListRow): boolean {
    if (!this.selection || this.selection.source !== row.source) return false;
    const idx = this.sourceIndex(row);
    if (row.source === 'vendor') {
      return this.currentItems[idx]?.itemId === this.selection.key;
    }
    return this.currentInventory[idx]?.slotIndex === this.selection.key;
  }

  private styleRowBg(row: ListRow): void {
    if (this.isRowSelected(row)) {
      row.bg.setFillStyle(ROW_FILL_SEL, ROW_FILL_SEL_ALPHA);
      row.bg.setStrokeStyle(1, GOLD_TEXT_COLOR_HEX);
    } else {
      row.bg.setFillStyle(ROW_FILL_HOVER, 0);
      row.bg.setStrokeStyle(1, 0x000000, 0);
    }
  }

  /**
   * Click/quick-action sobre una fila. Click simple → selecciona. Right-click
   * = comprar/vender 1. Shift+click = 5. Shift+right = Máx.
   */
  private handleRowInteraction(row: ListRow, mod: { isRight: boolean; shift: boolean }): void {
    const idx = this.sourceIndex(row);
    if (!this.itemAtRow(row)) return;

    if (mod.isRight || mod.shift) {
      let qty = 1;
      if (mod.shift && mod.isRight) qty = this.computeMaxForItem(row.source, idx);
      else if (mod.shift) qty = 5;
      // right-click sin shift → 1

      if (row.source === 'vendor') {
        const item = this.currentItems[idx];
        if (!item) return;
        if (item.buyPrice * qty > this.currentGold) {
          // No alcanza: caer a selección normal para que el botón muestre el costo.
          this.selectRow(row);
          return;
        }
        this.callbacks.onBuy(item.itemId, qty);
      } else {
        const inv = this.currentInventory[idx];
        if (!inv) return;
        const tmpl = getItemTemplate(inv.itemId);
        if (!tmpl || getEffectiveSellPrice(tmpl) <= 0) return;
        this.callbacks.onSell(inv.slotIndex, Math.min(qty, inv.quantity));
      }
      return;
    }

    this.selectRow(row);
  }

  private selectRow(row: ListRow): void {
    const idx = this.sourceIndex(row);
    if (row.source === 'vendor') {
      const item = this.currentItems[idx];
      if (!item) return;
      this.selection = { source: 'vendor', key: item.itemId };
    } else {
      const inv = this.currentInventory[idx];
      if (!inv) return;
      this.selection = { source: 'inventory', key: inv.slotIndex };
    }
    this.quantity = 1;
    this.refresh();
  }

  // === QUANTITY ==============================================================

  private setQuantity(q: number): void {
    const max = this.computeMaxQuantity();
    this.quantity = Math.max(1, Math.min(max, Math.floor(q)));
    this.refresh();
  }

  private computeMaxQuantity(): number {
    if (!this.selection) return 1;
    if (this.selection.source === 'vendor') {
      const item = this.currentItems.find((i) => i.itemId === this.selection!.key);
      if (!item || item.buyPrice <= 0) return 1;
      const tmpl = getItemTemplate(item.itemId);
      if (tmpl?.unique || tmpl?.affixable) return 1;
      return Math.max(1, Math.floor(this.currentGold / item.buyPrice));
    }
    const inv = this.currentInventory.find((i) => i.slotIndex === this.selection!.key);
    return inv ? Math.max(1, inv.quantity) : 1;
  }

  /** Cantidad máx comprable/vendible del item en `arrayIndex` (sin tocar selección). */
  private computeMaxForItem(source: SelectionSource, arrayIndex: number): number {
    if (source === 'vendor') {
      const item = this.currentItems[arrayIndex];
      if (!item || item.buyPrice <= 0) return 1;
      const tmpl = getItemTemplate(item.itemId);
      if (tmpl?.unique || tmpl?.affixable) return 1;
      return Math.max(1, Math.floor(this.currentGold / item.buyPrice));
    }
    const inv = this.currentInventory[arrayIndex];
    return inv ? Math.max(1, inv.quantity) : 1;
  }

  private isChipActive(value: number | null): boolean {
    const max = this.computeMaxQuantity();
    if (value === null) return this.quantity >= max && max > 1;
    return this.quantity === value && this.quantity < max;
  }

  private styleChip(chip: QtyChip): void {
    const active = this.isChipActive(chip.value);
    chip.text.setColor(active ? '#222222' : '#cccccc');
    chip.text.setBackgroundColor(active ? '#ffd166' : '#2a2a3a');
  }

  // === ACTIONS ===============================================================

  private executeBuy(): void {
    if (this.selection?.source !== 'vendor') return;
    const item = this.currentItems.find((i) => i.itemId === this.selection!.key);
    if (!item) return;
    const total = item.buyPrice * this.quantity;
    if (total > this.currentGold) return;
    this.callbacks.onBuy(item.itemId, this.quantity);
  }

  private executeSell(): void {
    if (this.selection?.source !== 'inventory') return;
    const inv = this.currentInventory.find((i) => i.slotIndex === this.selection!.key);
    if (!inv) return;
    const tmpl = getItemTemplate(inv.itemId);
    if (!tmpl || getEffectiveSellPrice(tmpl) <= 0) return;
    this.callbacks.onSell(inv.slotIndex, Math.min(this.quantity, inv.quantity));
  }

  /** Ejecuta la acción según la selección actual (Enter). */
  private executeAction(): void {
    if (this.selection?.source === 'vendor') this.executeBuy();
    else if (this.selection?.source === 'inventory') this.executeSell();
  }

  // === KEYBOARD ==============================================================

  private attachKeyboard(): void {
    if (this.keyHandler) return;
    this.keyHandler = (event: KeyboardEvent) => {
      if (!this.isOpen) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      switch (event.key) {
        case 'Escape':
          this.close();
          event.preventDefault();
          break;
        case 'Enter':
          this.executeAction();
          event.preventDefault();
          break;
        case '1':
          this.setQuantity(1);
          event.preventDefault();
          break;
        case '5':
          this.setQuantity(5);
          event.preventDefault();
          break;
        case '0':
          this.setQuantity(10);
          event.preventDefault();
          break;
        case 'm':
        case 'M':
          this.setQuantity(this.computeMaxQuantity());
          event.preventDefault();
          break;
      }
    };
    window.addEventListener('keydown', this.keyHandler);
  }

  private detachKeyboard(): void {
    if (!this.keyHandler) return;
    window.removeEventListener('keydown', this.keyHandler);
    this.keyHandler = null;
  }

  // === RENDER ================================================================

  private refresh(): void {
    this.goldLabelText.setText(formatThousands(this.currentGold));
    this.renderVendorList();
    this.renderInventoryList();
    this.renderQtyChips();
    this.renderActionButtons();
  }

  private renderVendorList(): void {
    for (const row of this.vendorRows) {
      const item = this.currentItems[this.sourceIndex(row)];
      if (!item) {
        this.clearRow(row);
        continue;
      }
      const tmpl = getItemTemplate(item.itemId);
      this.applyRowIcon(row.icon, item.itemIcon);
      row.icon.setVisible(true);
      const rarity = tmpl ? nominalRarity(tmpl) : ItemRarity.COMMON;
      row.name.setVisible(true).setColor(this.rarityColorHex(rarity));
      this.fitText(row.name, tmpl?.name ?? item.itemId, row.name.getData('maxW') as number);
      row.desc.setVisible(true);
      this.fitText(row.desc, tmpl?.description ?? '', row.desc.getData('maxW') as number);
      // Cantidad = precio unitario en oro (dorado).
      row.qty.setVisible(true).setText(formatThousands(item.buyPrice)).setColor(GOLD_TEXT_COLOR);
      this.styleRowBg(row);
    }
  }

  private renderInventoryList(): void {
    for (const row of this.invRows) {
      const inv = this.currentInventory[this.sourceIndex(row)];
      if (!inv) {
        this.clearRow(row);
        continue;
      }
      const tmpl = getItemTemplate(inv.itemId);
      this.applyRowIcon(row.icon, tmpl?.icon ?? 'noicon');
      row.icon.setVisible(true);
      const rarity = inv.rolledRarity ?? ItemRarity.COMMON;
      row.name.setVisible(true).setColor(this.rarityColorHex(rarity));
      this.fitText(row.name, tmpl?.name ?? inv.itemId, row.name.getData('maxW') as number);
      row.desc.setVisible(true);
      this.fitText(row.desc, tmpl?.description ?? '', row.desc.getData('maxW') as number);
      // Cantidad de stack (si > 1).
      row.qty.setVisible(true).setText(inv.quantity > 1 ? `x${inv.quantity}` : '').setColor('#ffffff');
      this.styleRowBg(row);
    }
  }

  private clearRow(row: ListRow): void {
    row.icon.setVisible(false);
    row.name.setVisible(false).setText('');
    row.desc.setVisible(false).setText('');
    row.qty.setVisible(false).setText('');
    row.bg.setFillStyle(ROW_FILL_HOVER, 0).setStrokeStyle(1, 0x000000, 0);
  }

  private renderQtyChips(): void {
    for (const chip of this.qtyChips) this.styleChip(chip);
  }

  private renderActionButtons(): void {
    // Comprar: activo si hay un item del vendor seleccionado.
    if (this.selection?.source === 'vendor') {
      const item = this.currentItems.find((i) => i.itemId === this.selection!.key);
      if (item) {
        const total = item.buyPrice * this.quantity;
        const canAfford = this.currentGold >= total;
        this.styleActionButton(
          this.buyBtnBg, this.buyBtnLabel, this.buyBtnIcon, this.buyBtnAmount, this.buyBtn,
          `Comprar ${this.quantity}`, formatThousands(total),
          canAfford ? '#ffffff' : '#888888', canAfford ? 0x2a3a1f : 0x2a2222, canAfford,
        );
      }
    } else {
      this.styleActionButton(
        this.buyBtnBg, this.buyBtnLabel, this.buyBtnIcon, this.buyBtnAmount, this.buyBtn,
        'Comprar', null, '#888888', 0x262626, false,
      );
    }

    // Vender: activo si hay un item del inventario seleccionado y es vendible.
    if (this.selection?.source === 'inventory') {
      const inv = this.currentInventory.find((i) => i.slotIndex === this.selection!.key);
      const tmpl = inv ? getItemTemplate(inv.itemId) : undefined;
      const sellPrice = tmpl ? getEffectiveSellPrice(tmpl) : 0;
      if (inv && sellPrice > 0) {
        const qty = Math.min(this.quantity, inv.quantity);
        this.styleActionButton(
          this.sellBtnBg, this.sellBtnLabel, this.sellBtnIcon, this.sellBtnAmount, this.sellBtn,
          `Vender ${qty}`, formatThousands(sellPrice * qty), '#ffffff', 0x3a2a14, true,
        );
      } else {
        this.styleActionButton(
          this.sellBtnBg, this.sellBtnLabel, this.sellBtnIcon, this.sellBtnAmount, this.sellBtn,
          'No vendible', null, '#888888', 0x332222, false,
        );
      }
    } else {
      this.styleActionButton(
        this.sellBtnBg, this.sellBtnLabel, this.sellBtnIcon, this.sellBtnAmount, this.sellBtn,
        'Vender', null, '#888888', 0x262626, false,
      );
    }
  }

  /**
   * Actualiza un botón de acción (bg auto-ancho + label + oro opcional) y lo
   * centra dentro de su container. Si `amount` es null, oculta ícono+monto.
   */
  private styleActionButton(
    bg: Phaser.GameObjects.Rectangle,
    label: Phaser.GameObjects.Text,
    icon: Phaser.GameObjects.Sprite,
    amount: Phaser.GameObjects.Text,
    _container: Phaser.GameObjects.Container,
    labelText: string,
    amountText: string | null,
    labelColor: string,
    bgColor: number,
    enabled: boolean,
  ): void {
    const PAD_X = 16;
    const PAD_Y = 7;
    const GAP = 6;
    label.setText(labelText).setColor(labelColor);
    let totalW = label.width;
    if (amountText !== null) {
      amount.setText(amountText);
      icon.setVisible(true);
      amount.setVisible(true);
      totalW += GAP + icon.displayWidth + amount.width;
    } else {
      icon.setVisible(false);
      amount.setVisible(false);
    }
    const bgW = totalW + PAD_X * 2;
    const bgH = Math.max(label.height, 16) + PAD_Y * 2;
    bg.setSize(bgW, bgH).setFillStyle(bgColor, 1);
    let x = -totalW / 2;
    label.setPosition(x, 0);
    x += label.width + GAP;
    if (amountText !== null) {
      icon.setPosition(x, 0);
      x += icon.displayWidth;
      amount.setPosition(x, 0);
    }
    if (enabled) bg.setInteractive({ useHandCursor: true });
    else bg.disableInteractive();
  }

  // === PORTRAITS =============================================================

  private refreshPortraits(merchantNameFallback: string): void {
    const npcVisual = this.currentNpcId
      ? this.callbacks.getNpcVisual?.(this.currentNpcId) ?? null
      : null;
    const npcName = npcVisual?.name?.trim() || merchantNameFallback;
    this.vendorNameText.setText(npcName);
    this.vendorPortraitSprite = this.applyPortraitSprite(
      this.vendorPortraitSprite,
      this.vendorPortraitCX,
      this.vendorPortraitCY,
      npcVisual?.headId ?? 0,
      npcVisual?.bodyId ?? 0,
    );

    const playerVisual = this.callbacks.getLocalPlayerVisual?.() ?? null;
    this.playerNameText.setText(playerVisual?.name?.trim() || 'Tú');
    this.playerPortraitSprite = this.applyPortraitSprite(
      this.playerPortraitSprite,
      this.playerPortraitCX,
      this.playerPortraitCY,
      playerVisual?.headId ?? 0,
      playerVisual?.bodyId ?? 0,
    );
    // Los sprites del portrait se crean acá la primera vez — re-aplicar el
    // filtro para que no aparezcan duplicados en cameras.main.
    this.applyCameraFilter();
  }

  private applyPortraitSprite(
    existing: Phaser.GameObjects.Sprite | null,
    cx: number,
    cy: number,
    headId: number,
    bodyId: number,
  ): Phaser.GameObjects.Sprite | null {
    let textureKey: string | null = null;
    let frame = 0;

    if (headId > 0 && this.scene.textures.exists(getHeadSpriteKey(headId))) {
      textureKey = getHeadSpriteKey(headId);
      frame = getHeadFrameForDirection(Direction.DOWN);
    } else if (bodyId > 0 && this.scene.textures.exists(getBodySpriteKey(bodyId))) {
      textureKey = getBodySpriteKey(bodyId);
      frame = getBodyIdleFrame(Direction.DOWN, bodyId);
    }

    if (!textureKey) {
      existing?.setVisible(false);
      return existing;
    }

    let sprite = existing;
    if (!sprite) {
      sprite = this.scene.add
        .sprite(cx, cy, textureKey, frame)
        .setOrigin(0.5)
        .setDepth(DEPTH_FG)
        .setScrollFactor(0);
    } else {
      sprite.setTexture(textureKey, frame).setPosition(cx, cy).setVisible(true);
    }
    const fw = Math.max(1, sprite.width);
    const fh = Math.max(1, sprite.height);
    const scale = Math.min(PORTRAIT_W / fw, PORTRAIT_H / fh);
    sprite.setScale(scale);
    return sprite;
  }

  // === HELPERS ===============================================================

  /**
   * Convierte el color de rareza a hex. COMMON = blanco (legible). Fallback
   * amarillo si la rareza es inválida.
   */
  private rarityColorHex(rarity: number): string {
    if (rarity === ItemRarity.COMMON) return '#ffffff';
    const num = ITEM_RARITY_COLORS[rarity as ItemRarity];
    if (typeof num !== 'number') return '#ffff88';
    return `#${num.toString(16).padStart(6, '0')}`;
  }

  /** Setea el ícono de una fila (1:1 a 32×32). */
  private applyRowIcon(sprite: Phaser.GameObjects.Image, iconName: string): void {
    const key = `item_icon_${iconName}`;
    sprite.setTexture(this.scene.textures.exists(key) ? key : PLACEHOLDER_ICON);
    sprite.setDisplaySize(ROW_ICON, ROW_ICON);
  }

  /** Trunca un texto a `maxW` px agregando "…" si hace falta. */
  private fitText(t: Phaser.GameObjects.Text, s: string, maxW: number): void {
    t.setText(s);
    if (t.width <= maxW || s.length <= 1) return;
    let str = s;
    while (str.length > 1 && t.width > maxW) {
      str = str.slice(0, -2);
      t.setText(`${str}…`);
    }
  }

  // === VISIBILITY / MOVE =====================================================

  private setVisible(visible: boolean): void {
    const els: Array<{ setVisible: (v: boolean) => unknown }> = [
      this.bg, this.title, this.closeBtn, this.divider,
      this.vendorPortraitBg, this.vendorNameText,
      this.playerPortraitBg, this.playerNameText,
      this.vendorListBg, this.invListBg,
      this.goldLabel, this.qtyLabel,
      this.buyBtn, this.sellBtn, this.hintText,
    ];
    for (const el of els) el.setVisible(visible);
    for (const chip of this.qtyChips) chip.text.setVisible(visible);
    this.vendorPortraitSprite?.setVisible(visible && this.vendorPortraitSprite.texture.key !== '__MISSING');
    this.playerPortraitSprite?.setVisible(visible && this.playerPortraitSprite.texture.key !== '__MISSING');
    for (const row of [...this.vendorRows, ...this.invRows]) {
      row.bg.setVisible(visible);
      if (!visible) {
        row.icon.setVisible(false);
        row.name.setVisible(false);
        row.desc.setVisible(false);
        row.qty.setVisible(false);
      }
    }
  }

  private movePanel(newX: number, newY: number): void {
    const dx = newX - this.panelX;
    const dy = newY - this.panelY;
    if (dx === 0 && dy === 0) return;
    this.panelX = newX;
    this.panelY = newY;
    const items: Array<{ x: number; y: number }> = [
      this.bg, this.title, this.closeBtn, this.divider,
      this.vendorPortraitBg, this.vendorNameText,
      this.playerPortraitBg, this.playerNameText,
      this.vendorListBg, this.invListBg,
      this.goldLabel, this.qtyLabel,
      this.buyBtn, this.sellBtn, this.hintText,
      ...this.qtyChips.map((c) => c.text),
      ...this.vendorRows.flatMap((r) => [r.bg, r.icon, r.name, r.desc, r.qty]),
      ...this.invRows.flatMap((r) => [r.bg, r.icon, r.name, r.desc, r.qty]),
    ];
    if (this.vendorPortraitSprite) items.push(this.vendorPortraitSprite);
    if (this.playerPortraitSprite) items.push(this.playerPortraitSprite);
    for (const el of items) {
      el.x += dx;
      el.y += dy;
    }
    this.vendorPortraitCX += dx;
    this.vendorPortraitCY += dy;
    this.playerPortraitCX += dx;
    this.playerPortraitCY += dy;
  }
}
